import React from 'react'
import { cn } from '@/lib/utils'

interface PageHeaderProps {
  /** Mono-uppercase eyebrow text — e.g. "Security", "Legal", "Docs". */
  eyebrow?: string
  /** Main heading. */
  title: string
  /** Optional subhead paragraph. */
  description?: React.ReactNode
  /** Optional right-aligned slot for badges, status pills, etc. */
  actions?: React.ReactNode
  className?: string
}

/**
 * Shared landing-page header. Replaces the ad-hoc "Badge + h1 + p" pattern
 * the static pages used. Keeps the existing JetBrains Mono heading font and
 * neutral palette; adds only an eyebrow + visual rhythm.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        'flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between',
        className,
      )}
    >
      <div className="min-w-0">
        {eyebrow && (
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            {eyebrow}
          </div>
        )}
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
          {title}
        </h1>
        {description && (
          <p className="mt-3 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </header>
  )
}

/**
 * Soft section divider — replaces `<Separator />` thin white lines with a
 * subtle gradient hairline that fades into the background at both ends.
 */
export function SectionDivider({ className }: { className?: string }) {
  return (
    <div
      role="presentation"
      className={cn(
        'h-px w-full bg-gradient-to-r from-transparent via-foreground/15 to-transparent',
        className,
      )}
    />
  )
}
